import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

/** Eén keuze in `Tabs` of `Segmented`. */
export interface ChoiceItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  /** Aantal achter het label (alleen `Tabs`). */
  count?: number;
  title?: string;
  /** Losse aan/uit-knop (lagen): eigen stand in plaats van `value === item.value`. */
  pressed?: boolean;
}

/** Pijltjes links/rechts verplaatsen de focus binnen de groep; Home/End naar de eerste/laatste. */
function moveFocus(e: KeyboardEvent<HTMLDivElement>) {
  const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
  if (!keys.includes(e.key)) return;
  const buttons = [...e.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
  const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
  if (i < 0) return;
  e.preventDefault();
  const next =
    e.key === "Home"
      ? 0
      : e.key === "End"
        ? buttons.length - 1
        : (i + (e.key === "ArrowRight" ? 1 : -1) + buttons.length) % buttons.length;
  buttons[next]?.focus();
}

/**
 * Tabs die niet in de breedte passen (telefoon): de gekozen tab schuift in beeld en `data-more`
 * zegt aan welke kant nog tabs staan, zodat de CSS daar een zachte rand tekent.
 */
function useTabsOverflow(value: string | null) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mark = () => {
      const max = el.scrollWidth - el.clientWidth;
      const start = el.scrollLeft > 4;
      const end = el.scrollLeft < max - 4;
      if (max <= 4) delete el.dataset.more;
      else el.dataset.more = start && end ? "both" : start ? "start" : "end";
    };
    const on = el.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (on && el.scrollWidth > el.clientWidth) {
      const left = on.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
      if (left < el.scrollLeft || left + on.offsetWidth > el.scrollLeft + el.clientWidth)
        el.scrollLeft = left - (el.clientWidth - on.offsetWidth) / 2;
    }
    // Onderstreping schuift naar de gekozen tab (UX-RICHTLIJNEN §8); zonder gekozen tab geen lijn.
    const place = () => {
      const cur = el.querySelector<HTMLElement>('[aria-pressed="true"]');
      if (!cur) return delete el.dataset.ind;
      el.style.setProperty("--ind-x", `${cur.offsetLeft}px`);
      el.style.setProperty("--ind-w", `${cur.offsetWidth}px`);
      el.dataset.ind = "1";
    };
    place();
    mark();
    el.addEventListener("scroll", mark, { passive: true });
    const ro = new ResizeObserver(() => {
      mark();
      place();
    });
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", mark);
      ro.disconnect();
    };
  }, [value]);
  return ref;
}

/**
 * Tabs (onderstreept): wisselen van weergave of pagina-onderdeel, zoals de gebouwweergaven,
 * Lijst/Kaart of Metingen/Gebrekenlijsten.
 */
export function Tabs<T extends string>({
  label,
  items,
  value,
  onChange,
  className = "",
}: {
  label: string;
  items: ChoiceItem<T>[];
  value: T | null;
  onChange: (value: T) => void;
  className?: string;
}) {
  const ref = useTabsOverflow(value);
  return (
    <div ref={ref} className={`tabs ${className}`} role="group" aria-label={label} onKeyDown={moveFocus}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          aria-pressed={it.pressed ?? it.value === value}
          title={it.title}
          onClick={() => onChange(it.value)}
        >
          {it.icon}
          {it.label}
          {it.count != null && <span className="count num">{it.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Segment: een keuze binnen een weergave (verdieping, conditie, thema, lagen). `radio` geeft
 * radioknop-semantiek (precies één keuze, zoals het thema).
 */
export function Segmented<T extends string>({
  label,
  items,
  value,
  onChange,
  radio = false,
  className = "",
}: {
  label: string;
  items: ChoiceItem<T>[];
  value?: T | null;
  onChange: (value: T) => void;
  radio?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`segmented ${className}`}
      role={radio ? "radiogroup" : "group"}
      aria-label={label}
      onKeyDown={moveFocus}
    >
      {items.map((it) => {
        const on = it.pressed ?? it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            className="segbtn"
            {...(radio ? { role: "radio", "aria-checked": on } : { "aria-pressed": on })}
            title={it.title}
            onClick={() => onChange(it.value)}
          >
            {it.icon}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
